import { SUPPORT_MESSAGES_PAGE_SIZE, type SupportMessageSummary } from "../../../hooks/useAdminSupportMessages";
import { formatDateTime, formatDateTimeCompact } from "../../../utils/formatDateTime";
import styles from "../../../pages/AdminInstances.module.css";
import { agentLabel } from "./SupportMessagePanel";
import own from "./SupportMessages.module.css";

export const SUPPORT_MESSAGE_HEADERS: ReadonlyArray<string> = ["Agent", "Date", "Type", "Subject", "Status"];

type Props = {
  readonly messages: ReadonlyArray<SupportMessageSummary>;
  readonly selectedId: string | null;
  readonly onOpen: (id: string) => void;
};

export const SupportMessagesTable = ({ messages, selectedId, onOpen }: Props): JSX.Element => (
  <table className={styles.table}>
    <thead>
      <tr>
        {SUPPORT_MESSAGE_HEADERS.map((h) => (
          <th key={h}>{h}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {messages.map((m) => (
        <SupportMessageRow key={m.id} message={m} selected={m.id === selectedId} onOpen={() => onOpen(m.id)} />
      ))}
    </tbody>
  </table>
);

type PagerProps = {
  readonly messages: ReadonlyArray<SupportMessageSummary>;
  readonly paged: boolean;
  readonly onOlder: (lastId: string) => void;
  readonly onNewest: () => void;
};

// Rendered apart from the table, so Newest survives an older page that came back empty.
export const SupportMessagesPager = ({ messages, paged, onOlder, onNewest }: PagerProps): JSX.Element | null => {
  const last = messages.length >= SUPPORT_MESSAGES_PAGE_SIZE ? messages[messages.length - 1] : undefined;
  if (!paged && last === undefined) return null;
  return (
    <div className={own.pager}>
      {paged && (
        <button type="button" className={styles.secondaryBtn} onClick={onNewest}>
          Newest
        </button>
      )}
      {last !== undefined && (
        <button type="button" className={styles.secondaryBtn} onClick={() => onOlder(last.id)}>
          Older
        </button>
      )}
    </div>
  );
};

type RowProps = {
  readonly message: SupportMessageSummary;
  readonly selected: boolean;
  readonly onOpen: () => void;
};

const SupportMessageRow = ({ message: m, selected, onOpen }: RowProps): JSX.Element => {
  const reviewed = m.reviewedAt !== null;
  return (
    <tr
      className={own.row}
      tabIndex={0}
      data-selected={selected}
      data-reviewed={reviewed}
      data-testid="support-message"
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <td>
        <span className={own.agent}>
          <span>{agentLabel(m)}</span>
          {m.agentName !== null && <span className={own.muted}>{m.tenantName ?? m.tenantId}</span>}
        </span>
      </td>
      <td className={styles.nowrap} title={formatDateTime(m.createdAt)}>
        {formatDateTimeCompact(m.createdAt)}
      </td>
      <td>
        <span className={styles.pill}>{m.kind}</span>
      </td>
      <td className={own.subjectCell} data-testid="support-message-subject">
        <span className={own.subjectText} title={m.subject}>
          {m.subject}
        </span>
      </td>
      <td className={own.status} data-reviewed={reviewed}>
        {reviewed ? "Reviewed" : "Unreviewed"}
      </td>
    </tr>
  );
};
